import { VisibilityFacet, CollisionFacet, CollisionShapeLocalTransformFacet, ModelChildChangedMessage, LayoutUpdatedMessage, worldToLocalTransform } from "flavr-common";
import { Matrix4 } from "flavr-math";
import { CreateCollisionShapeMessage, RemoveCollisionShapeMessage, UpdateCollisionShapeLocalTransformMessage, UpdateCollisionShapeSelectableStateMessage } from "@messages/selection";
import { modelPool, useExternalMessages, waitForModel } from "@modules/model";
import { busService } from "@modules/bus";
/**
 * This service is meant to hold virtual states of models and its corresponding visual representation (e.g. if an object
 * is held or not or if an object can be used or not)
 */
export class TrainingModelStateService {
    constructor() {
        this.handleModelChildChangedMessage = (message) => {
            for (const scenarioObject of this.copyColliderScenarioObjectsByParentUID.values()) {
                if (!this.hasCompoundParentCollision(scenarioObject.modelUid))
                    continue;
                if (!this.isModelInSubtree(message.uid, scenarioObject.modelUid) && !this.isModelInSubtree(message.childUID, scenarioObject.modelUid))
                    continue;
                this.rebuildCompoundCollisionForScenarioObject(scenarioObject);
            }
        };
        this.connection = busService.connection("TrainingModelStateService");
        this.modelStatesMap = new Map();
        this.copyColliderScenarioObjectsByParentUID = new Map();
        this.savedCollisionInfoMap = new Map();
        this.hiddenCollisionStateMap = new Map();
        this.pendingChildColliderDisableUIDs = new Set();
        this.pendingSavedCollisionStateMap = new Map();
        this.connection.subscribeByFacetType(CollisionFacet, (uid) => {
            this.retryPendingCollisionOperations(uid);
            this.redisableStateChildrenInSubtree(uid);
        });
        this.connection.subscribeByType(ModelChildChangedMessage, this.handleModelChildChangedMessage);
        this.connection.subscribeByType(LayoutUpdatedMessage, () => {
            this.modelStatesMap.clear();
            this.copyColliderScenarioObjectsByParentUID.clear();
            this.savedCollisionInfoMap.clear();
            this.hiddenCollisionStateMap.clear();
            this.pendingChildColliderDisableUIDs.clear();
            this.pendingSavedCollisionStateMap.clear();
        });
    }
    addScenarioObject(scenarioObject) {
        const currentState = scenarioObject.getCurrentState();
        if (currentState === undefined)
            return;
        this.modelStatesMap.set(scenarioObject.id, currentState);
        if (scenarioObject.copyCollider) {
            this.copyColliderScenarioObjectsByParentUID.set(scenarioObject.modelUid, scenarioObject);
            this.initializeSavedCollisionInfo(scenarioObject);
        }
        // Hide all models except for the current model
        this.createTrainingModel(scenarioObject, currentState);
    }
    onTrainingObjectStateChanged(event) {
        this.updateModelState(event.scenarioObject);
    }
    /// Private helper methods ///
    updateModelState(scenarioObject) {
        const previousState = this.modelStatesMap.get(scenarioObject.id);
        if (previousState === undefined) {
            console.warn(`[training] No model state registered for scenario object "${scenarioObject.id}"`);
            return;
        }
        const nextState = scenarioObject.getCurrentState();
        if (nextState === undefined) {
            console.warn(`[training] Next model state for scenario object "${scenarioObject.id}" not found`);
            return;
        }
        // Check if model changed
        const previousModelUID = previousState.modelUid;
        const nextModelUID = nextState.modelUid;
        const modelHasChanged = previousModelUID !== nextModelUID;
        // Hide model of old state and show model of new state
        if (modelHasChanged) {
            const messages = [];
            // Hide previous model
            if (previousModelUID !== undefined)
                this.updateModelVisibility(previousModelUID, false, messages);
            // Show next model
            if (nextModelUID !== undefined)
                this.updateModelVisibility(nextModelUID, true, messages);
            if (scenarioObject.copyCollider) {
                // Drive the parent collider from the cached child collider state, or clear it if no collider was authored.
                this.applySavedCollisionToParent(scenarioObject, nextState, messages);
            }
            // If copyCollider is false, colliders are left exactly as authored - no changes on state switch.
            this.connection.sendMultiple(messages);
        }
        this.modelStatesMap.set(scenarioObject.id, nextState);
    }
    createTrainingModel(scenarioObject, currentState) {
        var _a, _b;
        const uid = scenarioObject.modelUid;
        const model = modelPool.getModel(uid);
        if (model === undefined) {
            console.warn(`[training] Model ${uid} not defined for scenario object ${scenarioObject.id}`);
            return;
        }
        // Hide all models except for the model associated with the current state
        const messages = [];
        const childModelUIDs = (_b = (_a = scenarioObject.states) === null || _a === void 0 ? void 0 : _a.map(state => state.modelUid).filter((modelUID) => modelUID !== undefined)) !== null && _b !== void 0 ? _b : [];
        for (const childModelUID of childModelUIDs) {
            const visible = childModelUID === currentState.modelUid;
            if (scenarioObject.copyCollider)
                this.disableChildCollider(childModelUID, messages);
            this.updateModelVisibility(childModelUID, visible, messages);
        }
        if (scenarioObject.copyCollider)
            this.applySavedCollisionToParent(scenarioObject, currentState, messages);
        if (messages.length > 0)
            this.connection.sendMultiple(messages);
    }
    /// Facet messaging helper methods ///
    /**
     * Disables a child's own collider (so it isn't independently interactable). The CollisionFacet might be on a
     * nested descendant rather than childModelUID itself, so the message has to target whichever model actually owns it.
     */
    disableChildCollider(childModelUID, messages) {
        const model = modelPool.getModel(childModelUID);
        if (model === undefined) {
            this.pendingChildColliderDisableUIDs.add(childModelUID);
            return;
        }
        const collisionModel = this.findModelWithCollisionFacet(model);
        if (collisionModel === undefined) {
            this.pendingChildColliderDisableUIDs.add(childModelUID);
            return;
        }
        const facet = collisionModel.getFacet(CollisionFacet);
        if (facet === undefined)
            return; // unreachable, findModelWithCollisionFacet already checked
        this.pendingChildColliderDisableUIDs.delete(childModelUID);
        // Keep the child's own facet in sync in-memory (not persisted - only setFacet would push this to the server,
        // same reasoning as CompoundColliderManager mutating a facet's shape directly). This matters for compound
        // shapes in particular: CollisionModule doesn't build/commit a compound's CollisionShape synchronously - it
        // registers with CompoundColliderManager and the actual geometry build/commit happens later via a chain of
        // microtask-deferred async work. Previously this method sent a CreateCollisionShapeMessage carrying facet.shape
        // directly, which for a compound facet whose elements hadn't resolved yet meant shape was still the empty
        // placeholder array - crashing downstream in createColliderCompoundGeometry ("Compound geometry list is empty")
        // and silently dropping the disable for that child. Mutating the facet first means whichever build eventually
        // commits (in-flight or future) already sees isSelectable/isTrigger false, and the message below only needs to
        // update a collision shape that already exists - it never carries or rebuilds geometry, so it can't race
        // CompoundColliderManager's own shape rebuilds, and no-ops harmlessly if the shape isn't built yet.
        facet.isSelectable = false;
        facet.isTrigger = false;
        messages.push(new UpdateCollisionShapeSelectableStateMessage(collisionModel.getUID(), false, false));
    }
    initializeSavedCollisionInfo(scenarioObject) {
        var _a;
        const savedCollisionInfos = new Map();
        this.savedCollisionInfoMap.set(scenarioObject.id, savedCollisionInfos);
        for (const state of (_a = scenarioObject.states) !== null && _a !== void 0 ? _a : []) {
            this.captureSavedCollisionInfoForState(scenarioObject, state);
        }
    }
    captureSavedCollisionInfoForState(scenarioObject, state) {
        const savedCollisionInfos = this.savedCollisionInfoMap.get(scenarioObject.id);
        if (savedCollisionInfos === undefined)
            return;
        if (state.modelUid === undefined) {
            savedCollisionInfos.set(state.name, null);
            this.pendingSavedCollisionStateMap.delete(this.getPendingStateKey(scenarioObject, state));
            return;
        }
        if (modelPool.getModel(state.modelUid) === undefined) {
            waitForModel(state.modelUid, () => {
                if (this.copyColliderScenarioObjectsByParentUID.get(scenarioObject.modelUid) !== scenarioObject)
                    return;
                this.captureSavedCollisionInfoForState(scenarioObject, state);
                const activeState = scenarioObject.getCurrentState();
                if ((activeState === null || activeState === void 0 ? void 0 : activeState.name) !== state.name)
                    return;
                const deferredMessages = [];
                this.applySavedCollisionToParent(scenarioObject, activeState, deferredMessages);
                if (deferredMessages.length > 0)
                    this.connection.sendMultiple(deferredMessages);
            });
            return;
        }
        const collisionInfo = this.computeTrainingModelCollisionInfo(state.modelUid, scenarioObject.modelUid);
        const pendingStateKey = this.getPendingStateKey(scenarioObject, state);
        if (collisionInfo === undefined) {
            this.pendingSavedCollisionStateMap.set(pendingStateKey, { scenarioObject, state });
            return;
        }
        this.pendingSavedCollisionStateMap.delete(pendingStateKey);
        savedCollisionInfos.set(state.name, {
            isSelectable: collisionInfo.isSelectable,
            isTrigger: collisionInfo.isTrigger,
            shape: collisionInfo.shape,
            localTransform: collisionInfo.localTransform
        });
    }
    applySavedCollisionToParent(scenarioObject, state, messages) {
        const savedCollisionInfos = this.savedCollisionInfoMap.get(scenarioObject.id);
        if (savedCollisionInfos === undefined) {
            messages.push(new RemoveCollisionShapeMessage(scenarioObject.modelUid));
            return;
        }
        if (!savedCollisionInfos.has(state.name))
            this.captureSavedCollisionInfoForState(scenarioObject, state);
        const collisionInfo = savedCollisionInfos.get(state.name);
        if (collisionInfo === undefined) {
            return;
        }
        if (collisionInfo === null) {
            messages.push(new RemoveCollisionShapeMessage(scenarioObject.modelUid));
            return;
        }
        const isSelectable = collisionInfo.isSelectable;
        const isTrigger = collisionInfo.isTrigger;
        const parentModel = modelPool.getModel(scenarioObject.modelUid);
        const parentCollisionFacet = parentModel === null || parentModel === void 0 ? void 0 : parentModel.getFacet(CollisionFacet);
        if (parentCollisionFacet !== undefined) {
            parentCollisionFacet.shape = this.cloneCollisionShape(collisionInfo.shape);
            parentCollisionFacet.isSelectable = isSelectable;
            parentCollisionFacet.isTrigger = isTrigger;
        }
        const parentLocalTransformFacet = parentModel === null || parentModel === void 0 ? void 0 : parentModel.getFacet(CollisionShapeLocalTransformFacet);
        if (parentLocalTransformFacet !== undefined)
            parentLocalTransformFacet.localTransform = [...collisionInfo.localTransform];
        messages.push(new CreateCollisionShapeMessage(this.cloneCollisionShape(collisionInfo.shape), scenarioObject.modelUid, isSelectable, isTrigger), new UpdateCollisionShapeSelectableStateMessage(scenarioObject.modelUid, isSelectable, isTrigger), new UpdateCollisionShapeLocalTransformMessage(scenarioObject.modelUid, collisionInfo.localTransform));
    }
    updateModelVisibility(modelUID, visible, messages) {
        const model = modelPool.getModel(modelUID);
        if (model === undefined)
            return;
        this.updateModelVisibilityChildren(model, visible, messages);
    }
    updateModelVisibilityChildren(parent, visible, messages) {
        var _a, _b;
        const collisionModel = this.findModelWithCollisionFacet(parent);
        if (collisionModel !== undefined) {
            const collisionFacet = collisionModel.getFacet(CollisionFacet);
            if (collisionFacet !== undefined) {
                const uid = collisionModel.getUID();
                if (!visible) {
                    if (!this.hiddenCollisionStateMap.has(uid))
                        this.hiddenCollisionStateMap.set(uid, { isSelectable: collisionFacet.isSelectable, isTrigger: collisionFacet.isTrigger });
                    messages.push(new UpdateCollisionShapeSelectableStateMessage(uid, false, false));
                }
                else {
                    const previousState = this.hiddenCollisionStateMap.get(uid);
                    const isSelectable = (_a = previousState === null || previousState === void 0 ? void 0 : previousState.isSelectable) !== null && _a !== void 0 ? _a : collisionFacet.isSelectable;
                    const isTrigger = (_b = previousState === null || previousState === void 0 ? void 0 : previousState.isTrigger) !== null && _b !== void 0 ? _b : collisionFacet.isTrigger;
                    messages.push(new UpdateCollisionShapeSelectableStateMessage(uid, isSelectable, isTrigger));
                    this.hiddenCollisionStateMap.delete(uid);
                }
            }
        }
        parent.setFacet(new VisibilityFacet(visible), useExternalMessages(messages));
        for (const child of parent.getChildren()) {
            this.updateModelVisibilityChildren(child, visible, messages);
        }
    }
    computeTrainingModelCollisionInfo(childUID, parentUID) {
        const model = modelPool.getModel(childUID);
        if (model === undefined)
            return undefined;
        const parentModel = modelPool.getModel(parentUID);
        if (parentModel === undefined)
            return undefined;
        const collisionModel = this.findModelWithCollisionFacet(model);
        if (collisionModel === undefined)
            return undefined;
        const collisionFacet = collisionModel.getFacet(CollisionFacet);
        if (collisionFacet === undefined)
            return undefined; // unreachable, findModelWithCollisionFacet already checked
        const localTransformFacet = collisionModel.getFacet(CollisionShapeLocalTransformFacet);
        const collisionLocalTransform = localTransformFacet !== undefined
            ? new Matrix4().fromArray(localTransformFacet.localTransform)
            : new Matrix4();
        const collisionShapeWorldTransform = new Matrix4().multiplyMatrices(collisionModel.getWorldTransform(), collisionLocalTransform);
        const collisionShapeParentLocalTransform = worldToLocalTransform(collisionShapeWorldTransform, parentModel.getWorldTransform(), new Matrix4());
        return {
            isSelectable: collisionFacet.isSelectable,
            isTrigger: collisionFacet.isTrigger,
            shape: this.cloneCollisionShape(collisionFacet.shape),
            localTransform: collisionShapeParentLocalTransform.toArray(),
        };
    }
    cloneCollisionShape(shape) {
        if (CollisionFacet.isPrimitiveShape(shape))
            return shape;
        if (CollisionFacet.isMeshShape(shape)) {
            return {
                shape: shape.shape,
                meshUrl: shape.meshUrl,
                pieceName: shape.pieceName
            };
        }
        return shape.map((element) => {
            const clonedShape = CollisionFacet.isPrimitiveShape(element.shape)
                ? element.shape
                : {
                    shape: element.shape.shape,
                    meshUrl: element.shape.meshUrl,
                    pieceName: element.shape.pieceName
                };
            return {
                shape: clonedShape,
                trans: {
                    pos: [...element.trans.pos],
                    ori: [...element.trans.ori],
                    scale: [...element.trans.scale]
                },
                uid: element.uid
            };
        });
    }
    getParentCollisionInfo(parentUID) {
        const parentModel = modelPool.getModel(parentUID);
        if (parentModel === undefined)
            return undefined;
        const collisionFacet = parentModel.getFacet(CollisionFacet);
        if (collisionFacet === undefined)
            return undefined;
        // A compound collider's merged geometry is already expressed in the parent's own local space - each element's
        // transform is computed relative to this same model by CompoundColliderManager - so it needs no additional
        // offset on top. A CollisionShapeLocalTransformFacet here was meant for a single primitive/mesh collider that
        // needs its own positional offset; blindly reapplying it to a compound can silently shrink/move the whole thing
        // (e.g. a stale/degenerate near-zero scale left over from before this model became a compound).
        if (CollisionFacet.isCompoundShape(collisionFacet.shape)) {
            return { facet: collisionFacet, localTransform: new Matrix4() };
        }
        const colliderLocalTransformFacet = parentModel.getFacet(CollisionShapeLocalTransformFacet);
        const colliderLocalTransform = (colliderLocalTransformFacet !== undefined)
            ? new Matrix4().fromArray(colliderLocalTransformFacet.localTransform)
            : new Matrix4();
        return {
            facet: collisionFacet,
            localTransform: colliderLocalTransform
        };
    }
    hasCompoundParentCollision(parentUID) {
        const parentCollisionInfo = this.getParentCollisionInfo(parentUID);
        if (parentCollisionInfo === undefined)
            return false;
        return CollisionFacet.isCompoundShape(parentCollisionInfo.facet.shape);
    }
    rebuildCompoundCollisionForScenarioObject(scenarioObject) {
        var _a, _b;
        const currentState = scenarioObject.getCurrentState();
        if (currentState === undefined)
            return;
        for (const state of (_a = scenarioObject.states) !== null && _a !== void 0 ? _a : []) {
            this.captureSavedCollisionInfoForState(scenarioObject, state);
        }
        const messages = [];
        for (const state of (_b = scenarioObject.states) !== null && _b !== void 0 ? _b : []) {
            if (state.modelUid === undefined)
                continue;
            this.disableChildCollider(state.modelUid, messages);
        }
        this.applySavedCollisionToParent(scenarioObject, currentState, messages);
        if (messages.length > 0)
            this.connection.sendMultiple(messages);
    }
    findModelWithCollisionFacet(model) {
        if (model.getFacet(CollisionFacet) !== undefined)
            return model;
        for (const child of model.getChildren()) {
            const found = this.findModelWithCollisionFacet(child);
            if (found !== undefined)
                return found;
        }
        return undefined;
    }
    retryPendingCollisionOperations(collisionFacetUID) {
        var _a;
        const messages = [];
        // A copyCollider scenario object's own parent CollisionFacet might not have synced in yet the first time
        // applySavedCollisionToParent ran (e.g. during addScenarioObject, before the parent model's own facets finished
        // loading). Unlike the per-state child capture below, there was no retry for that case, so the initial state's
        // collider could silently stay missing/non-selectable forever once the early attempt no-op'd.
        const scenarioObjectForParent = this.copyColliderScenarioObjectsByParentUID.get(collisionFacetUID);
        if (scenarioObjectForParent !== undefined) {
            const activeState = scenarioObjectForParent.getCurrentState();
            if (activeState !== undefined)
                this.applySavedCollisionToParent(scenarioObjectForParent, activeState, messages);
        }
        for (const [pendingKey, pendingState] of Array.from(this.pendingSavedCollisionStateMap.entries())) {
            const stateModelUID = pendingState.state.modelUid;
            if (stateModelUID === undefined || !this.isModelInSubtree(collisionFacetUID, stateModelUID))
                continue;
            this.captureSavedCollisionInfoForState(pendingState.scenarioObject, pendingState.state);
            const savedCollisionInfo = (_a = this.savedCollisionInfoMap.get(pendingState.scenarioObject.id)) === null || _a === void 0 ? void 0 : _a.get(pendingState.state.name);
            if (savedCollisionInfo === undefined)
                continue;
            this.pendingSavedCollisionStateMap.delete(pendingKey);
            const activeState = pendingState.scenarioObject.getCurrentState();
            if ((activeState === null || activeState === void 0 ? void 0 : activeState.name) === pendingState.state.name)
                this.applySavedCollisionToParent(pendingState.scenarioObject, activeState, messages);
        }
        // Cache authored selectable/trigger flags before disabling child colliders, otherwise we can accidentally
        // persist the already-disabled false/false state for late-resolving models.
        for (const childModelUID of Array.from(this.pendingChildColliderDisableUIDs)) {
            if (!this.isModelInSubtree(collisionFacetUID, childModelUID))
                continue;
            this.disableChildCollider(childModelUID, messages);
        }
        if (messages.length > 0)
            this.connection.sendMultiple(messages);
    }
    /**
     * A state child's own CollisionFacet can (re-)sync independently of TrainingModelStateService at any time - not
     * just before disableChildCollider ever succeeded for it (pendingChildColliderDisableUIDs above only covers that
     * first-resolution case). If a state child's facet fires again later, CollisionModule's own facet handler
     * recreates/updates its collision shape using the authored (non-disabled) isSelectable/isTrigger, silently
     * undoing a disable that already succeeded, since nothing else would ever reapply it. Re-disable proactively
     * whenever a copyCollider scenario object's state child collision facet changes, deferred to a microtask so this
     * always runs after CollisionModule's own (synchronous-up-to-its-first-await) handling of the same facet-changed
     * event, so our disable message gets a newer mutation version and wins the race.
     */
    redisableStateChildrenInSubtree(collisionFacetUID) {
        var _a;
        for (const scenarioObject of this.copyColliderScenarioObjectsByParentUID.values()) {
            const affectedStateModelUIDs = ((_a = scenarioObject.states) !== null && _a !== void 0 ? _a : [])
                .map((state) => state.modelUid)
                .filter((modelUID) => modelUID !== undefined)
                .filter((modelUID) => this.isModelInSubtree(collisionFacetUID, modelUID));
            if (affectedStateModelUIDs.length === 0)
                continue;
            queueMicrotask(() => {
                if (this.copyColliderScenarioObjectsByParentUID.get(scenarioObject.modelUid) !== scenarioObject)
                    return;
                const messages = [];
                for (const modelUID of affectedStateModelUIDs) {
                    this.disableChildCollider(modelUID, messages);
                }
                if (messages.length > 0)
                    this.connection.sendMultiple(messages);
            });
        }
    }
    isModelInSubtree(modelUID, rootUID) {
        const model = modelPool.getModel(modelUID);
        const rootModel = modelPool.getModel(rootUID);
        if (model === undefined || rootModel === undefined)
            return false;
        return model.isDescendantOfOrEquals(rootModel);
    }
    getPendingStateKey(scenarioObject, state) {
        return `${scenarioObject.id}::${state.name}`;
    }
}
